import api from '../api';
import { uploadUrl } from './uploads';

const PDF_EXT = /\.pdf(\?.*)?$/i;
const WORD_EXT = /\.docx?(\?.*)?$/i;
const TEXT_EXT = /\.(txt|text|rtf)(\?.*)?$/i;
const OPAQUE_STORAGE_NAME = /^[a-f0-9]{16,64}$/i;

const MIME_BY_KIND = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  text: 'text/plain; charset=utf-8'
};

export function resumeFileKind(pathOrName) {
  const p = (pathOrName || '').toLowerCase().split('?')[0];
  if (PDF_EXT.test(p)) return 'pdf';
  if (WORD_EXT.test(p)) return 'doc';
  if (TEXT_EXT.test(p)) return 'text';
  return 'unknown';
}

export function isOpaqueStorageName(name) {
  if (!name) return true;
  const base = String(name).split('/').pop().split('?')[0].trim();
  if (!base) return true;
  if (OPAQUE_STORAGE_NAME.test(base)) return true;
  if (!/\./.test(base) && base.length > 24) return true;
  return false;
}

export function resumeDisplayName(src, fileName) {
  if (fileName && !isOpaqueStorageName(fileName)) return fileName;
  if (!src) return 'Resume';
  if (src.startsWith('blob:')) return 'Uploaded resume';
  const part = src.split('/').pop() || src;
  if (isOpaqueStorageName(part)) return 'Resume';
  try {
    const decoded = decodeURIComponent(part.split('?')[0]);
    return isOpaqueStorageName(decoded) ? 'Resume' : decoded;
  } catch {
    return isOpaqueStorageName(part) ? 'Resume' : part;
  }
}

function sanitizeApiFileName(name, fallback = 'Resume') {
  if (!name || isOpaqueStorageName(name)) return fallback;
  return name;
}

function resolveHref(src) {
  if (!src) return null;
  if (src.startsWith('blob:') || src.startsWith('http')) return src;
  const path = uploadUrl(src);
  if (path.startsWith('/')) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

export function sanitizeResumeDownloadBaseName(name) {
  return String(name || 'Candidate')
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, ' ')
    .trim() || 'Candidate';
}

/** «Candidate Name - Cand-0001.pdf» */
export function buildCandidateResumeFileName({ candidateName, candidateId, kind, fileName }) {
  const base = sanitizeResumeDownloadBaseName(candidateName);
  const id = String(candidateId || '').trim() || 'unknown';
  let ext = '';
  if (fileName && /\.\w{2,5}$/i.test(fileName)) {
    ext = fileName.match(/(\.\w{2,5})$/i)[1].toLowerCase();
  } else if (kind === 'pdf') ext = '.pdf';
  else if (kind === 'docx' || kind === 'doc') ext = '.docx';
  else if (kind === 'text') ext = '.txt';
  else ext = '.pdf';
  return `${base} - ${id}${ext}`;
}

function parseContentDispositionFileName(header) {
  if (!header) return '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* ignore */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || '';
}

export async function downloadCandidateResumeViaApi(candidateId, { candidateName } = {}) {
  if (!candidateId) throw new Error('Candidate id required');
  const res = await api.get(`/candidates/${candidateId}/resume/download`, {
    responseType: 'blob'
  });
  const fromHeader = parseContentDispositionFileName(res.headers['content-disposition']);
  const kind = await sniffBlobKind(res.data);
  const fileName =
    fromHeader ||
    buildCandidateResumeFileName({
      candidateName,
      candidateId,
      kind,
      fileName: fromHeader
    });
  return { blob: res.data, fileName: ensureDownloadFileName(fileName, kind) };
}

async function sniffBlobKind(blob) {
  const head = await blob.slice(0, 8).arrayBuffer();
  const u = new Uint8Array(head);
  if (u.length >= 5 && u[0] === 0x25 && u[1] === 0x50 && u[2] === 0x44 && u[3] === 0x46) return 'pdf';
  if (u.length >= 4 && u[0] === 0x50 && u[1] === 0x4b) return 'docx';
  if (u.length >= 4 && u[0] === 0xd0 && u[1] === 0xcf && u[2] === 0x11 && u[3] === 0xe0) return 'doc';
  const sample = await blob.slice(0, Math.min(blob.size, 4096)).arrayBuffer();
  const bytes = new Uint8Array(sample);
  const textLike = bytes.filter(b => b === 9 || b === 10 || b === 13 || (b >= 32 && b <= 126)).length;
  if (bytes.length && textLike / bytes.length > 0.85) return 'text';
  return 'unknown';
}

function blobWithMime(blob, kind) {
  const mime =
    kind === 'docx' ? MIME_BY_KIND.docx : kind === 'doc' ? MIME_BY_KIND.doc : MIME_BY_KIND[kind];
  if (!mime || blob.type === mime) return blob;
  return new Blob([blob], { type: mime });
}

export async function fetchResumeBlob(src) {
  const url = resolveHref(src);
  if (!url) throw new Error('No resume file');
  const res = await fetch(url);
  if (!res.ok) throw new Error('Could not load resume file');
  let blob = await res.blob();
  const sniffed = await sniffBlobKind(blob);
  const extKind = resumeFileKind(src);
  const kind = sniffed !== 'unknown' ? sniffed : extKind;
  blob = blobWithMime(blob, kind === 'docx' ? 'docx' : kind);
  return { blob, kind };
}

/** Fetch stored upload and return a typed blob object URL safe for iframe preview (no forced download). */
export async function fetchResumeBlobUrl(src) {
  const { blob, kind } = await fetchResumeBlob(src);
  return { url: URL.createObjectURL(blob), kind, blob };
}

function uploadNameForBlob(blob, fileName, src, sniffed) {
  const fromName = fileName && !isOpaqueStorageName(fileName) ? fileName : '';
  if (fromName) return fromName;
  const kind = sniffed || resumeFileKind(fileName || src);
  if (kind === 'pdf') return 'resume.pdf';
  if (kind === 'docx' || kind === 'doc') return 'resume.docx';
  if (kind === 'text') return 'resume.txt';
  return 'resume.dat';
}

export function ensureDownloadFileName(name, kind) {
  let base = String(name || 'resume').trim();
  if (!base || isOpaqueStorageName(base)) base = 'resume';
  if (!/\.\w{2,5}$/i.test(base)) {
    if (kind === 'pdf') base += '.pdf';
    else if (kind === 'docx' || kind === 'doc') base += '.docx';
    else if (kind === 'text') base += '.txt';
  }
  return base;
}

/** Programmatic download — avoids broken `<a href="blob:...">` after revokeObjectURL. */
export function triggerFileDownload(blob, fileName = 'resume') {
  if (!blob) throw new Error('No file to download');
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}

/** Resolve blob + filename for download (local file, blob URL, or stored upload path). */
export async function resolveResumeDownloadBlob({
  src,
  file,
  fileName,
  candidateName,
  candidateId,
  cachedBlob
}) {
  const display = resumeDisplayName(src, fileName);
  const named = (kind, name = fileName) =>
    ensureDownloadFileName(
      candidateName || candidateId
        ? buildCandidateResumeFileName({ candidateName, candidateId, kind, fileName: name || display })
        : name || display,
      kind
    );

  if (cachedBlob instanceof Blob) {
    const sniffed = await sniffBlobKind(cachedBlob);
    return { blob: cachedBlob, fileName: named(sniffed, fileName || display) };
  }

  if (file instanceof File || file instanceof Blob) {
    const sniffed = await sniffBlobKind(file);
    return {
      blob: file,
      fileName: named(sniffed, file.name || fileName || display)
    };
  }

  if (
    candidateId &&
    src &&
    !src.startsWith('blob:') &&
    (src.includes('/uploads/') || src.startsWith('/uploads'))
  ) {
    try {
      return await downloadCandidateResumeViaApi(candidateId, { candidateName });
    } catch (apiErr) {
      if (!src.startsWith('blob:')) {
        /* fall through to fetch */
      } else {
        throw apiErr;
      }
    }
  }

  if (src?.startsWith('blob:')) {
    const res = await fetch(src);
    if (!res.ok) throw new Error('Could not read resume file — upload it again or save the profile first');
    const blob = await res.blob();
    const sniffed = await sniffBlobKind(blob);
    return {
      blob,
      fileName: named(sniffed, fileName || display)
    };
  }

  if (src) {
    const { blob, kind } = await fetchResumeBlob(src);
    return {
      blob,
      fileName: named(kind, fileName || display)
    };
  }

  throw new Error('No resume file to download');
}

/** Load PDF iframe URL or HTML preview (PDF, DOCX, DOC, TXT — including extensionless uploads). */
export async function loadResumePreview({ src, file, fileName }) {
  const display = resumeDisplayName(src, fileName);
  const meaningfulName = fileName && !isOpaqueStorageName(fileName) ? fileName : '';

  if (!file && src && (src.startsWith('/uploads/') || src.includes('/uploads/'))) {
    try {
      const { data } = await api.post('/resume/preview-stored', {
        path: src.startsWith('/') ? src : `/${src}`,
        fileName: meaningfulName,
        originalName: meaningfulName
      });
      if (data.kind === 'pdf') {
        const { url, blob } = await fetchResumeBlobUrl(src);
        return {
          kind: 'pdf',
          href: url,
          revoke: true,
          fileName: sanitizeApiFileName(data.fileName, display),
          blob
        };
      }
      if (data.kind === 'html' && data.html) {
        return {
          kind: 'html',
          html: data.html,
          fileName: sanitizeApiFileName(data.fileName, display)
        };
      }
    } catch (storedErr) {
      const msg = storedErr.response?.data?.message;
      if (msg) throw new Error(msg);
    }
  }

  const fd = new FormData();
  let blobForPdf = null;
  let sniffed = 'unknown';

  if (file instanceof File || file instanceof Blob) {
    fd.append('resume', file, file.name || meaningfulName || 'resume');
    blobForPdf = file;
    sniffed = await sniffBlobKind(file);
  } else if (src) {
    const { url, kind, blob } = await fetchResumeBlobUrl(src);
    blobForPdf = blob;
    sniffed = kind;
    const uploadName = uploadNameForBlob(blob, meaningfulName, src, sniffed);
    fd.append('resume', blob, uploadName);
    if (sniffed === 'pdf') {
      return {
        kind: 'pdf',
        href: url,
        revoke: true,
        fileName: display,
        blob
      };
    }
  } else {
    throw new Error('No resume file to preview');
  }

  const { data } = await api.post('/resume/preview-html', fd);
  if (data.kind === 'pdf') {
    const typed = blobWithMime(blobForPdf, 'pdf');
    return {
      kind: 'pdf',
      href: URL.createObjectURL(typed),
      revoke: true,
      fileName: sanitizeApiFileName(data.fileName, display),
      blob: typed
    };
  }
  return {
    kind: 'html',
    html: data.html,
    fileName: sanitizeApiFileName(data.fileName, display)
  };
}

export function revokePreviewUrl(preview) {
  if (preview?.revoke && preview.href?.startsWith('blob:')) {
    URL.revokeObjectURL(preview.href);
  }
}
